import { CURRENCY_CONFIG, Currency, PackageType, REVENUECAT_CONFIG } from "@/lib/revenuecat/config";
import { getPriceForCurrency } from "@/lib/revenuecat/currency-detector";

export const PACKAGE_IDENTIFIER_MAP: Record<PackageType, string[]> = {
  weekly: ["$rc_weekly", "$rc_weekly_20_off"],
  monthly: ["$rc_monthly", "$rc_monthly_20_off", "$rc_monthly_botes33"],
  annual: ["Annual Subscription", "$rc_annual", "$rc_annual_20_off", "$rc_annual_botes33"],
};

const DISCOUNT_PACKAGE_IDENTIFIER_MAP: Record<PackageType, string[]> = {
  weekly: ["$rc_weekly_20_off", "$rc_weekly"],
  monthly: ["$rc_monthly_botes33", "$rc_monthly_20_off", "$rc_monthly"],
  annual: ["$rc_annual_botes33", "$rc_annual_20_off", "Annual Subscription", "$rc_annual"],
};

const SDK_PACKAGE_TYPES: Record<PackageType, string[]> = {
  weekly: ["$rc_weekly"],
  monthly: ["$rc_monthly"],
  annual: ["$rc_annual", "Annual Subscription"],
};

const PACKAGE_PERIOD_DURATIONS: Record<PackageType, string[]> = {
  weekly: ["P1W"],
  monthly: ["P1M"],
  annual: ["P1Y"],
};

const PACKAGE_PERIOD_UNITS: Record<PackageType, string[]> = {
  weekly: ["week"],
  monthly: ["month"],
  annual: ["year"],
};

type RevenueCatPriceLike = {
  amount?: number;
  amountMicros?: number;
  currency?: string;
  formattedPrice?: string;
};

type RevenueCatPhaseLike = {
  cycleCount?: number;
  period?: {
    number?: number;
    unit?: string;
  } | null;
  periodDuration?: string | null;
  price?: RevenueCatPriceLike | null;
};

type RevenueCatProductLike = {
  currencyCode?: string;
  currentPrice?: RevenueCatPriceLike;
  freeTrialPhase?: RevenueCatPhaseLike | null;
  identifier?: string;
  introPricePhase?: RevenueCatPhaseLike | null;
  normalPeriodDuration?: string | null;
  period?: {
    unit?: string;
  } | null;
  price?: RevenueCatPriceLike | number;
  priceString?: string;
};

export type RevenueCatPackageLike = {
  identifier?: string;
  packageType?: string;
  product?: RevenueCatProductLike;
  rcBillingProduct?: RevenueCatProductLike;
  webBillingProduct?: RevenueCatProductLike;
};

type RevenueCatOfferingLike = {
  availablePackages?: RevenueCatPackageLike[];
};

type RevenueCatOfferingsLike = {
  current?: RevenueCatOfferingLike | null;
};

export type PackageDisplayPricing = {
  currencyCode: string;
  hasDiscountPrice: boolean;
  originalPriceFormatted?: string;
  priceAmount: number;
  priceFormatted: string;
  promotionalPeriod?: string;
  showFreeTrial: boolean;
};

export function getRevenueCatProduct(
  packageToPurchase: RevenueCatPackageLike | null | undefined
): RevenueCatProductLike | null {
  return (
    packageToPurchase?.webBillingProduct ??
    packageToPurchase?.rcBillingProduct ??
    packageToPurchase?.product ??
    null
  );
}

function getPriceFormatted(price: RevenueCatPriceLike | number | null | undefined) {
  if (!price || typeof price === "number") return undefined;
  return price.formattedPrice;
}

function getPriceAmount(price: RevenueCatPriceLike | number | null | undefined) {
  if (typeof price === "number" && Number.isFinite(price) && price > 0) {
    return price;
  }

  if (!price || typeof price === "number") return undefined;

  if (Number.isFinite(price.amountMicros) && price.amountMicros! > 0) {
    return price.amountMicros! / 1_000_000;
  }

  if (Number.isFinite(price.amount) && price.amount! > 0) {
    return price.amount! / 100;
  }

  return undefined;
}

function getPriceCurrency(price: RevenueCatPriceLike | number | null | undefined) {
  return price && typeof price !== "number" ? price.currency : undefined;
}

export function getRevenueCatPackagePriceFormatted(
  packageToPurchase: RevenueCatPackageLike | null | undefined
) {
  const product = getRevenueCatProduct(packageToPurchase);
  if (!product) return undefined;

  return (
    getPriceFormatted(product.price) ??
    getPriceFormatted(product.currentPrice) ??
    product.priceString ??
    undefined
  );
}

export function getRevenueCatPackagePriceAmount(
  packageToPurchase: RevenueCatPackageLike | null | undefined
) {
  const product = getRevenueCatProduct(packageToPurchase);
  if (!product) return undefined;

  const price = product.price ?? product.currentPrice;

  return getPriceAmount(price);
}

export function getRevenueCatPackageCurrency(
  packageToPurchase: RevenueCatPackageLike | null | undefined,
  fallbackCurrency: Currency
) {
  return getRevenueCatPackageCurrencyCode(packageToPurchase) ?? fallbackCurrency;
}

function getRevenueCatPackageCurrencyCode(
  packageToPurchase: RevenueCatPackageLike | null | undefined
) {
  const product = getRevenueCatProduct(packageToPurchase);
  const price = product?.price ?? product?.currentPrice;

  if (price && typeof price !== "number" && price.currency) {
    return price.currency;
  }

  return product?.currencyCode;
}

function pickPreferredCurrencyPackage(
  packages: Array<RevenueCatPackageLike | undefined>,
  preferredCurrency?: string,
  options: { allowFallback?: boolean } = {}
) {
  const availablePackages = packages.filter(Boolean) as RevenueCatPackageLike[];
  if (preferredCurrency) {
    const currencyMatch = availablePackages.find(
      (pkg) => getRevenueCatPackageCurrencyCode(pkg) === preferredCurrency
    );
    if (currencyMatch) return currencyMatch;
  }

  if (options.allowFallback === false) {
    return undefined;
  }

  return availablePackages[0];
}

export function findRevenueCatPackage(
  offering: RevenueCatOfferingLike | null | undefined,
  packageType: PackageType,
  options: { preferDiscount?: boolean; preferredCurrency?: string } = {}
) {
  const packages = offering?.availablePackages ?? [];
  const candidates = options.preferDiscount
    ? DISCOUNT_PACKAGE_IDENTIFIER_MAP[packageType]
    : PACKAGE_IDENTIFIER_MAP[packageType];
  const packageIdentifier = REVENUECAT_CONFIG.packages[packageType].identifier;
  const identifierPackages = candidates.map((candidate) =>
    packages.find((pkg) => pkg.identifier === candidate)
  );
  const sdkTypePackages = packages.filter((pkg) =>
    SDK_PACKAGE_TYPES[packageType].includes(pkg.packageType ?? "")
  );
  const periodPackages = packages.filter((pkg) => {
    const product = getRevenueCatProduct(pkg);
    const duration = product?.normalPeriodDuration;
    const unit = product?.period?.unit;
    return (
      (duration && PACKAGE_PERIOD_DURATIONS[packageType].includes(duration)) ||
      (unit && PACKAGE_PERIOD_UNITS[packageType].includes(unit))
    );
  });
  const matchingIdentifier = pickPreferredCurrencyPackage(
    identifierPackages,
    options.preferredCurrency,
    { allowFallback: false }
  );
  const matchingSdkType = pickPreferredCurrencyPackage(sdkTypePackages, options.preferredCurrency, {
    allowFallback: false,
  });
  const matchingPeriod = pickPreferredCurrencyPackage(periodPackages, options.preferredCurrency, {
    allowFallback: false,
  });

  const fallbackIdentifier = pickPreferredCurrencyPackage(identifierPackages);
  const fallbackSdkType = pickPreferredCurrencyPackage(sdkTypePackages);
  const fallbackPeriod = pickPreferredCurrencyPackage(periodPackages);

  return (
    matchingIdentifier ??
    matchingSdkType ??
    matchingPeriod ??
    fallbackIdentifier ??
    packages.find((pkg) => pkg.identifier === packageIdentifier) ??
    fallbackSdkType ??
    fallbackPeriod ??
    null
  );
}

export function findSelectableRevenueCatPackage({
  activeOffering,
  coupon,
  couponApplied,
  offerings,
  packageType,
  preferredCurrency,
}: {
  activeOffering: RevenueCatOfferingLike | null | undefined;
  coupon: string;
  couponApplied: boolean;
  offerings: RevenueCatOfferingsLike | null | undefined;
  packageType: PackageType;
  preferredCurrency?: string;
}) {
  const normalizedCoupon = coupon.toLowerCase();

  if (
    couponApplied &&
    (normalizedCoupon === "botes33" ||
      normalizedCoupon === "tickles33" ||
      normalizedCoupon === "abbs33") &&
    packageType === "weekly"
  ) {
    return findRevenueCatPackage(offerings?.current, packageType, { preferredCurrency });
  }

  return findRevenueCatPackage(activeOffering, packageType, {
    preferDiscount: couponApplied,
    preferredCurrency,
  });
}

function pluralizePeriodUnit(unit: string, value: number) {
  return value === 1 ? unit : `${unit}s`;
}

function formatPhasePeriod(phase: RevenueCatPhaseLike | null | undefined) {
  if (!phase?.cycleCount || phase.cycleCount < 1) return undefined;

  const periodNumber = phase.period?.number;
  const periodUnit = phase.period?.unit;
  if (periodNumber && periodUnit) {
    const total = periodNumber * phase.cycleCount;
    return `${total} ${pluralizePeriodUnit(periodUnit, total)}`;
  }

  return undefined;
}

export function getPackageDisplayPricing({
  currency,
  discountRequested,
  packageToPurchase,
  packageType,
}: {
  currency: Currency;
  discountRequested: boolean;
  packageToPurchase: RevenueCatPackageLike | null | undefined;
  packageType: PackageType;
}): PackageDisplayPricing {
  const fallbackPrice = getPriceForCurrency(packageType, currency);
  const product = getRevenueCatProduct(packageToPurchase);
  const basePriceFormatted =
    getRevenueCatPackagePriceFormatted(packageToPurchase) ?? fallbackPrice.formatted;
  const basePriceAmount =
    getRevenueCatPackagePriceAmount(packageToPurchase) ?? fallbackPrice.amount;
  const baseCurrencyCode = getRevenueCatPackageCurrencyCode(packageToPurchase) ?? currency;
  const introPricePhase = product?.introPricePhase?.price;
  const introPriceFormatted = getPriceFormatted(introPricePhase);
  const introPriceAmount = getPriceAmount(introPricePhase);
  const introCurrencyCode = getPriceCurrency(introPricePhase);

  if (discountRequested && introPriceFormatted) {
    return {
      currencyCode: introCurrencyCode ?? baseCurrencyCode,
      hasDiscountPrice: true,
      originalPriceFormatted:
        basePriceFormatted !== introPriceFormatted ? basePriceFormatted : undefined,
      priceAmount: introPriceAmount ?? basePriceAmount,
      priceFormatted: introPriceFormatted,
      promotionalPeriod: formatPhasePeriod(product?.introPricePhase),
      showFreeTrial: false,
    };
  }

  return {
    currencyCode: baseCurrencyCode,
    hasDiscountPrice: false,
    priceAmount: basePriceAmount,
    priceFormatted: basePriceFormatted,
    showFreeTrial: !discountRequested && !!product?.freeTrialPhase,
  };
}

export function getFallbackPackagePriceValue(packageType: PackageType, currency: Currency) {
  const packagePrices = CURRENCY_CONFIG[currency];
  if (packageType === "weekly") return packagePrices.weeklyPrice;
  if (packageType === "annual") return packagePrices.annualPrice;
  return packagePrices.monthlyPrice;
}

export function getAnnualSavingsPercent(monthlyAmount: number, annualAmount: number) {
  if (
    !Number.isFinite(monthlyAmount) ||
    !Number.isFinite(annualAmount) ||
    monthlyAmount <= 0 ||
    annualAmount <= 0
  ) {
    return null;
  }

  const annualizedMonthlyAmount = monthlyAmount * 12;
  const savingsPercent = Math.round(
    ((annualizedMonthlyAmount - annualAmount) / annualizedMonthlyAmount) * 100
  );

  return savingsPercent > 0 ? savingsPercent : null;
}

export function getComparableAnnualSavingsPercent({
  annualPricing,
  fallbackCurrency,
  monthlyPricing,
}: {
  annualPricing: Pick<PackageDisplayPricing, "currencyCode" | "priceAmount">;
  fallbackCurrency: Currency;
  monthlyPricing: Pick<PackageDisplayPricing, "currencyCode" | "priceAmount">;
}) {
  const monthlyCurrency = monthlyPricing.currencyCode.trim().toUpperCase();
  const annualCurrency = annualPricing.currencyCode.trim().toUpperCase();

  if (monthlyCurrency && monthlyCurrency === annualCurrency) {
    return getAnnualSavingsPercent(monthlyPricing.priceAmount, annualPricing.priceAmount);
  }

  return getAnnualSavingsPercent(
    getFallbackPackagePriceValue("monthly", fallbackCurrency),
    getFallbackPackagePriceValue("annual", fallbackCurrency)
  );
}

export function formatMonthlyEquivalent(priceAmount: number, currencyCode: string) {
  if (!Number.isFinite(priceAmount) || priceAmount <= 0) return null;

  const monthlyAmount = priceAmount / 12;
  const normalizedCurrency = currencyCode.trim().toUpperCase();

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(monthlyAmount);
  } catch {
    return `${normalizedCurrency || "USD"} ${monthlyAmount.toFixed(2)}`;
  }
}
